// Schedule or edit a meeting.
//
// THE FIRST QUESTION IS WHICH KIND, because every field after it depends on the answer: a
// client meeting needs a client (and may name a job), an internal one needs the other
// partner and can have neither. That is the shape the schema guards (ck_meetings_party)
// and the service rejects; the form simply never offers the invalid combination.
//
// THE OWNER IS NOT EDITED HERE on an existing meeting. Handing a meeting to the other
// partner is its own act with its own endpoint — it is the one edit that has to ask a
// DIFFERENT person's diary — so it lives on the meeting's own view, beside cancel and
// delete. On a NEW meeting the owner is just which diary is being booked, so it is a field.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { todayStr } from '../../utils/dates';
import { clientsApi, jobsApi } from '../../api';
import { meetingsApi } from '../../api/calendar';
import Modal from '../../components/Modal';
import { TextInput, DateInput, Select } from '../../components/Form';
import ClientPicker from '../../components/ClientPicker';
import { ErrorText } from '../../components/Loader';
import { isJobDone } from '../../components/StatusBadge';
import SlotPicker from './SlotPicker';
import { MEETING_KIND, DURATIONS } from './meetingKind';

export default function MeetingModal({ meeting, partners, me, onClose, onSaved }) {
  // A meeting can be with a lead as easily as with a customer — meeting a prospect is
  // what the pipeline's "פגישה" stage is about — so the picker needs every client, the
  // same read TaskModal makes and for the same reason.
  const { data } = useAsync(() => Promise.all([clientsApi.list('all'), jobsApi.list()]));
  const [clients, jobs] = data || [[], []];
  const isNew = !meeting.id;

  const [form, setForm] = useState({
    kind: meeting.kind || MEETING_KIND.CLIENT,
    client_id: meeting.client_id || '',
    related_job_id: meeting.related_job_id || '',
    // The diary defaults to the booker's own — when the booker HAS one. A secretary is not a
    // partner, so for her nothing is preselected and she chooses whose diary it is.
    owner_partner_id: meeting.owner_partner_id
      || (partners.some((p) => String(p.id) === String(me.id)) ? me.id : ''),
    with_partner_id: meeting.with_partner_id || '',
    // Someone outside the system (077): the name as typed, and optionally who they are.
    external_name: meeting.external_name || '',
    external_details: meeting.external_details || '',
    title: meeting.title || '',
    // DEFAULTS TO TODAY on a new meeting, and that is what makes the time picker useful the
    // moment the form opens: free slots cannot be computed without a day, so an empty date
    // meant the one control showing clock times had nothing to show and said so. Today is
    // the honest default — it is a real, checkable day, and it is one field to overwrite.
    //
    // Editing keeps the meeting's own date, and a booking started by clicking an empty slot
    // in the grid keeps the day that was clicked; both arrive with `meeting.meeting_date`
    // already set, so neither is touched by this.
    meeting_date: meeting.meeting_date || todayStr(),
    start_time: meeting.start_time || '',
    duration_minutes: meeting.duration_minutes || 60,
    // Carried through untouched and never shown: the new meeting's provenance when this
    // form was opened from a summary's "schedule a follow-up". There is no field for it
    // and nothing edits it — it only has to survive the save.
    follow_up_of_meeting_id: meeting.follow_up_of_meeting_id || '',
  });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const isInternal = form.kind === MEETING_KIND.INTERNAL;
  const isExternal = form.kind === MEETING_KIND.EXTERNAL;
  const isClient = !isInternal && !isExternal;

  // Switching kind clears the fields that belong to the other one, rather than leaving
  // them in state to be sent and rejected. The schema forbids a client on an internal
  // meeting; the form should never be able to propose one.
  const onKindChange = (e) => {
    const kind = e.target.value;
    setForm((f) => ({
      ...f,
      kind,
      ...(kind !== MEETING_KIND.CLIENT ? { client_id: '', related_job_id: '' } : {}),
      ...(kind !== MEETING_KIND.INTERNAL ? { with_partner_id: '' } : {}),
      ...(kind !== MEETING_KIND.EXTERNAL ? { external_name: '', external_details: '' } : {}),
    }));
  };

  // Picking a client resets the job, which is client-scoped.
  const onClientChange = (id) =>
    setForm((f) => ({ ...f, client_id: id, related_job_id: '' }));

  const kindOptions = [
    { value: MEETING_KIND.CLIENT, label: he.calendar.kindClient },
    { value: MEETING_KIND.INTERNAL, label: he.calendar.kindInternal },
    { value: MEETING_KIND.EXTERNAL, label: he.calendar.kindExternal },
  ];
  // The client's jobs that are still open, plus whichever this meeting already points at
  // even if finished — the same rule and the same reason as TaskModal's job picker: a
  // Select renders no placeholder, so dropping the selected value would show one job while
  // the state held another.
  const jobOptions = [
    { value: '', label: he.calendar.noJob },
    ...jobs
      .filter((j) => String(j.client_id) === String(form.client_id))
      .filter((j) => !isJobDone(j.status) || String(j.id) === String(form.related_job_id))
      .map((j) => ({ value: j.id, label: j.title })),
  ];
  const partnerOptions = [
    ...(form.owner_partner_id ? [] : [{ value: '', label: he.common.choose }]),
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ];
  const otherPartnerOptions = [
    { value: '', label: he.common.choose },
    ...partners.filter((p) => String(p.id) !== String(form.owner_partner_id))
      .map((p) => ({ value: p.id, label: p.name })),
  ];
  const durationOptions = DURATIONS.map((d) => ({ value: d, label: he.calendar.minutes(d) }));

  const owner = Number(form.owner_partner_id) || null;
  const ownerName = (partners.find((p) => p.id === owner) || {}).name || '';

  const submit = async () => {
    try {
      const payload = {
        kind: form.kind,
        client_id: isClient ? Number(form.client_id) || null : null,
        related_job_id: isClient ? Number(form.related_job_id) || null : null,
        external_name: isExternal ? form.external_name.trim() : null,
        external_details: isExternal ? form.external_details.trim() || null : null,
        owner_partner_id: owner,
        with_partner_id: isInternal ? Number(form.with_partner_id) || null : null,
        title: form.title || null,
        meeting_date: form.meeting_date,
        start_time: form.start_time,
        duration_minutes: Number(form.duration_minutes),
        follow_up_of_meeting_id: Number(form.follow_up_of_meeting_id) || null,
      };
      if (isNew) await meetingsApi.create(payload);
      else await meetingsApi.update(meeting.id, payload);
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  const incomplete = !owner
    || !form.meeting_date
    || !form.start_time
    || (isInternal && !form.with_partner_id)
    || (isExternal && !form.external_name.trim())
    || (isClient && !form.client_id);

  return (
    <Modal
      title={isNew ? he.calendar.newMeeting : he.calendar.editMeeting}
      onClose={onClose}
      onSave={submit}
      saveDisabled={incomplete}
    >
      <Select label={he.calendar.kind} value={form.kind} onChange={onKindChange} options={kindOptions} />

      {isInternal && (
        <Select
          label={he.calendar.withPartner}
          value={form.with_partner_id}
          onChange={set('with_partner_id')}
          options={otherPartnerOptions}
        />
      )}
      {isClient && (
        <>
          <ClientPicker label={he.calendar.client} value={form.client_id} onChange={onClientChange} clients={clients} />
          <Select label={he.calendar.job} value={form.related_job_id} onChange={set('related_job_id')} options={jobOptions} />
        </>
      )}
      {isExternal && (
        <>
          <TextInput label={he.calendar.externalName} value={form.external_name} onChange={set('external_name')} />
          <TextInput
            label={he.calendar.externalDetails}
            value={form.external_details}
            onChange={set('external_details')}
            placeholder={he.calendar.externalDetailsPlaceholder}
          />
        </>
      )}

      <TextInput
        label={he.calendar.title_}
        value={form.title}
        onChange={set('title')}
        placeholder={he.calendar.titlePlaceholder}
      />

      {/* The owner is only a field while the meeting is being created. Afterwards it moves
          to its own action — see the header comment. */}
      {isNew && (
        <Select
          label={he.calendar.owner}
          value={form.owner_partner_id}
          onChange={(e) => setForm((f) => ({
            ...f,
            owner_partner_id: e.target.value,
            // The other side of an internal meeting can never be the owner.
            with_partner_id: String(f.with_partner_id) === e.target.value ? '' : f.with_partner_id,
            // The diary being asked has changed, so any time already chosen is about the
            // wrong person. Clearing it forces a fresh pick from the new partner's grid
            // rather than carrying a time that may now be taken.
            start_time: '',
          }))}
          options={partnerOptions}
        />
      )}

      {/* WHEN: the day, then the time of day, then how long it runs — in that order, and
          the order is the fix for a real confusion. The duration select used to sit above
          the slot picker, so the FIRST time-looking control on the form was the one showing
          "שעה" and "1:30 שעות" while the one showing 08:00 / 08:30 came after it and was
          labelled only by whose diary it read. Anyone looking for a time found the length.

          The date comes first because free slots are meaningless without a day, and the
          duration last because it only narrows a list that already exists — changing it
          refetches the grid, which is cheap and visible. */}
      <DateInput label={he.calendar.date} value={form.meeting_date} onChange={set('meeting_date')} />

      {/* An internal meeting has to fit in BOTH diaries, so the picker is told about the
          other partner and the server returns the intersection. Without it the form would
          happily offer a time the other partner is busy in and only fail on save. */}
      <SlotPicker
        label={he.calendar.startTime}
        hint={isInternal
          ? he.calendar.slotsForBoth
          : (ownerName ? he.calendar.slotsFor(ownerName) : null)}
        date={form.meeting_date}
        partnerId={owner}
        withPartnerId={isInternal ? Number(form.with_partner_id) || null : null}
        duration={Number(form.duration_minutes)}
        excludeId={meeting.id}
        value={form.start_time}
        onChange={(time) => setForm((f) => ({ ...f, start_time: time }))}
      />

      <Select
        label={he.calendar.duration}
        value={form.duration_minutes}
        onChange={(e) => setForm((f) => ({ ...f, duration_minutes: Number(e.target.value) }))}
        options={durationOptions}
      />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
